/** Max `count` for GET .../sub-organizations/{start}/{count} (API-enforced limit). */
export const SUB_ORGANIZATION_MAX_PAGE_SIZE = 100;

/** `SubOrganizationStatus` codes from the API. */
export const SUB_ORGANIZATION_STATUS_CODE = {
	active: 1,
	closed: 2,
} as const;

/** `Statuses` query value that includes active and closed rows. */
export const SUB_ORGANIZATION_STATUSES_INCLUDING_CLOSED = `${SUB_ORGANIZATION_STATUS_CODE.active},${SUB_ORGANIZATION_STATUS_CODE.closed}`;

export const SubOrganizationStatusFilter = {
	Active: "active",
	Closed: "closed",
	All: "all",
} as const;

export type SubOrganizationStatusFilter =
	(typeof SubOrganizationStatusFilter)[keyof typeof SubOrganizationStatusFilter];

/** Query value for the admin list. Active is the default. */
export function subOrganizationStatusesParam(
	filter: SubOrganizationStatusFilter,
): string {
	if (filter === SubOrganizationStatusFilter.Closed) {
		return String(SUB_ORGANIZATION_STATUS_CODE.closed);
	}
	if (filter === SubOrganizationStatusFilter.All) {
		return SUB_ORGANIZATION_STATUSES_INCLUDING_CLOSED;
	}
	return String(SUB_ORGANIZATION_STATUS_CODE.active);
}

export function isClosedSubOrganization(
	statusCode: number | null | undefined,
): boolean {
	return statusCode === SUB_ORGANIZATION_STATUS_CODE.closed;
}

/**
 * Appends the closed suffix when the sub-organization is closed.
 * Blank names stay blank so callers can keep their empty-cell behavior.
 */
export function formatClosedSubOrganizationName(
	name: string | null | undefined,
	statusCode: number | null | undefined,
	closedLabel: string,
): string | undefined {
	const trimmed = name?.trim();
	if (!trimmed) return undefined;
	if (isClosedSubOrganization(statusCode)) {
		return `${trimmed} · ${closedLabel}`;
	}
	return trimmed;
}

/** `SubContractorStatus` codes from the API. */
export const SUB_CONTRACTOR_STATUS_CODE = {
	active: 1,
	closed: 2,
} as const;

/** `Statuses` query value that includes active and closed subcontractors. */
export const SUB_CONTRACTOR_STATUSES_INCLUDING_CLOSED = `${SUB_CONTRACTOR_STATUS_CODE.active},${SUB_CONTRACTOR_STATUS_CODE.closed}`;

export const SubContractorStatusFilter = {
	Active: "active",
	Closed: "closed",
	All: "all",
} as const;

export type SubContractorStatusFilter =
	(typeof SubContractorStatusFilter)[keyof typeof SubContractorStatusFilter];

/** Query value for the admin list. Active is the default. */
export function subContractorStatusesParam(
	filter: SubContractorStatusFilter,
): string {
	if (filter === SubContractorStatusFilter.Closed) {
		return String(SUB_CONTRACTOR_STATUS_CODE.closed);
	}
	if (filter === SubContractorStatusFilter.All) {
		return SUB_CONTRACTOR_STATUSES_INCLUDING_CLOSED;
	}
	return String(SUB_CONTRACTOR_STATUS_CODE.active);
}

export function isClosedSubContractor(
	statusCode: number | null | undefined,
): boolean {
	return statusCode === SUB_CONTRACTOR_STATUS_CODE.closed;
}

/** Which status-row action to show. Pending or denied results hide the pencil. */
export function subOrganizationStatusAction(input: {
	closeAllowed: boolean;
	reopenAllowed: boolean;
	closePending: boolean;
	reopenPending: boolean;
}): "close" | "reopen" | null {
	if (input.closePending || input.reopenPending) return null;
	if (input.closeAllowed) return "close";
	if (input.reopenAllowed) return "reopen";
	return null;
}

/** Delete stays hidden while the action check is pending or denied. */
export function showSubOrganizationDelete(input: {
	allowed: boolean;
	pending: boolean;
}): boolean {
	return input.allowed && !input.pending;
}

/** Delete stays hidden while the action check is pending or denied. */
export function showSubContractorDelete(input: {
	allowed: boolean;
	pending: boolean;
}): boolean {
	return input.allowed && !input.pending;
}
